// Prompt inspector: the exact prompt and render settings behind a tile,
// with one-click copy. The text is editable, and "Render this prompt" hands
// the edited version back to the app to reroll. Shared by the card and the
// fullscreen preview.

const modal = document.getElementById('prompt-modal');
const textEl = document.getElementById('prompt-text');
const metaEl = document.getElementById('prompt-meta');
const copyBtn = document.getElementById('prompt-copy');
const rerunBtn = document.getElementById('prompt-rerun');
const closeBtn = document.getElementById('prompt-close');

const svg = (path) =>
  `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#${path}"/></svg>`;
const ICON_COPY = svg('i-copy');
const ICON_CHECK = svg('i-check');

let config = null;
let entry = null;
let resubmit = null;

export function setPromptConfig(cfg) {
  config = cfg;
}

/** Register the handler that rerolls an edited prompt. Called with
 *  (promptText, entry) where entry is whatever openPromptModal was given. */
export function onPromptResubmit(fn) {
  resubmit = fn;
}

export function isPromptModalOpen() {
  return !modal.classList.contains('hidden');
}

export function closePromptModal() {
  modal.classList.add('hidden');
}

// A batch whose slots vary by style is submitted as ONE dynamic prompt —
// `{a|b|c}` — and the platform reports that combined string back for every
// job, without recording which branch a given tile used. So the inspector
// resolves each group to its first option: a real, runnable prompt instead of
// the whole alternation with its braces and pipes.
function resolveDynamic(prompt) {
  let out = String(prompt ?? '');
  // Innermost groups first; the guard caps pathological nesting.
  for (let guard = 0; guard < 5 && /\{[^{}]*\}/.test(out); guard++) {
    out = out.replace(/\{([^{}]*)\}/g, (_, body) => body.split('|')[0]);
  }
  return out.trim();
}

// The settings menu already speaks in model names, so the inspector does too;
// an id the config no longer lists falls back to itself rather than hiding.
function modelLabel(id) {
  const m = config?.models?.find((x) => x.id === id);
  return m?.label || id;
}

// `name` is the plain subject name — captions carry extra detail (seed,
// scores, position) that would read as duplication here.
export function openPromptModal(item = {}) {
  if (!item.prompt) return;
  entry = item;
  textEl.value = resolveDynamic(item.prompt);
  const bits = [];
  if (item.name) bits.push(item.name);
  if (item.seed != null) bits.push(`seed ${item.seed}`);
  if (config) {
    // An upscaled tile carries its own size; everything else is the render size.
    const px = item.size || config.size;
    bits.push(modelLabel(item.model || config.model), `${px}×${px}`);
  }
  metaEl.textContent = bits.join('  ·  ');
  copyBtn.innerHTML = `${ICON_COPY} Copy prompt`;
  copyBtn.classList.remove('saved');
  modal.classList.remove('hidden');
}

copyBtn.onclick = async () => {
  try {
    await navigator.clipboard.writeText(textEl.value);
  } catch {
    // Clipboard blocked (permissions, insecure context) — select the text
    // so the user can copy it manually.
    textEl.focus();
    textEl.select();
    return;
  }
  copyBtn.innerHTML = `${ICON_CHECK} Copied`;
  copyBtn.classList.add('saved');
};

rerunBtn.onclick = () => {
  const prompt = textEl.value.trim();
  if (!prompt || !resubmit) return;
  closePromptModal();
  resubmit(prompt, entry || {});
};

closeBtn.onclick = closePromptModal;
modal.onclick = (e) => {
  if (e.target === modal) closePromptModal();
};
